// src/features/commodities-ranking/CommoditiesRankingPage.tsx
//
// Commodities ranking feature page. Sibling of
// features/etf-ranking/EtfRankingPage. Loads the per-category factor
// aggregates from GET /api/v1/commodities-ranking/category-summary once
// (drives the radar + the full category-filter pill list), and the ranked
// cross-section from GET /api/v1/commodities-ranking/universe. The universe
// is fetched SERVER-SIDE per category via the ?category= param: selecting
// "All Categories" loads the full curated payload, selecting a category
// pill refetches just that bucket's rows. Sorting then happens client-side
// on whatever set is loaded. A single batched call to
// GET /api/v1/commodities-ranking/historical-batch powers the table
// sparklines for the currently-visible rows (no N+1 waterfall). Clicking a
// ticker opens the COT positioning pop-over (the commodities analogue of
// the ETF quick-stats modal). Reuses the standard
// <Layout sidebar={<Sidebar />}> dashboard shell so it sits behind the same
// auth gate / navigation as the rest of the suite.

import { useEffect, useMemo, useState } from 'react';

import { Layout, Sidebar } from '../../shared/ui';
import {
    fetchCategorySummary,
    fetchHistoricalBatch,
    fetchUniverse,
    type CategorySummaryRow,
    type HistoricalBatchResponse,
    type UniverseRow,
} from './api';
import { CategoryRadar } from './components/CategoryRadar';
import { CotPopup } from './components/CotPopup';
import { RiskReturnScatter } from './components/RiskReturnScatter';
import { UniverseTable, type SortKey } from './components/UniverseTable';

// Cap the batched sparkline fetch to the top N rows of the current
// sort/filter. The curated commodities universe is small (~23 names) so
// this rarely bites, but it keeps the contract identical to the ETF page.
const SPARKLINE_LIMIT = 60;

export const CommoditiesRankingPage = () => {
    const [universe, setUniverse] = useState<UniverseRow[]>([]);
    const [categorySummary, setCategorySummary] = useState<
        CategorySummaryRow[]
    >([]);
    const [asOfDate, setAsOfDate] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [isUniverseLoading, setIsUniverseLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [activeCategory, setActiveCategory] = useState<string | null>(null);
    const [sortKey, setSortKey] = useState<SortKey>('universe_rank');
    const [sortDesc, setSortDesc] = useState<boolean>(false);
    const [hoverSymbol, setHoverSymbol] = useState<string | null>(null);
    // Ticker clicked in the table — drives the COT positioning pop-over.
    const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);

    const [sparklines, setSparklines] = useState<HistoricalBatchResponse>({});

    // --- Category aggregates: loaded ONCE on mount. ---------------------
    // Drives both the radar chart and the full filter-pill list, so the
    // pill list stays complete even when the universe is narrowed to a
    // single category by the server-side ?category= refetch below.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchCategorySummary();
                if (!cancelled) setCategorySummary(res.categories);
            } catch (err) {
                // Non-fatal: the universe effect owns the page-level error
                // state. A missing radar should not blank the whole page.
                // eslint-disable-next-line no-console
                console.warn('Failed to load category summary', err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // --- Universe: SERVER-SIDE refetch whenever the category changes. ---
    // Selecting a pill hits GET /commodities-ranking/universe?category=<name>;
    // "All Categories" (activeCategory === null) loads the full payload.
    // This replaces client-side filtering so the table, scatter and
    // sparkline batch all operate on exactly the rows the server returned.
    useEffect(() => {
        let cancelled = false;
        setIsUniverseLoading(true);
        (async () => {
            try {
                const res = await fetchUniverse(activeCategory ?? undefined);
                if (cancelled) return;
                setUniverse(res.items);
                setAsOfDate(res.as_of_date);
                setError(null);
            } catch (err) {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('Failed to load commodities ranking universe', err);
                setError(
                    'Unable to load commodities rankings right now. Please try again shortly.',
                );
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                    setIsUniverseLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [activeCategory]);

    // --- Distinct categories for the filter tabs (stable order). --------
    // Sourced from the category-summary aggregate (always the full set),
    // NOT from the universe — which may hold a single category's rows
    // after a server-side filter and would otherwise collapse the list.
    const categories = useMemo(
        () =>
            categorySummary
                .map((c) => c.category)
                .sort((a, b) => a.localeCompare(b)),
        [categorySummary],
    );

    // Full universe size = sum of per-category counts. Stable regardless
    // of which category the universe payload is currently filtered to, so
    // the "All Categories" pill always shows the true total.
    const totalUniverseSize = useMemo(
        () => categorySummary.reduce((acc, c) => acc + (c.count ?? 0), 0),
        [categorySummary],
    );

    // --- Client-side SORT only (filtering is server-side now). ----------
    const processed = useMemo(() => {
        const sorted = [...universe].sort((a, b) => {
            const va = a[sortKey];
            const vb = b[sortKey];
            let cmp: number;
            if (typeof va === 'string' && typeof vb === 'string') {
                cmp = va.localeCompare(vb);
            } else {
                cmp = (va as number) - (vb as number);
            }
            return sortDesc ? -cmp : cmp;
        });
        return sorted;
    }, [universe, sortKey, sortDesc]);

    // --- Symbols whose sparklines we batch-fetch (top of current view). -
    const sparklineSymbols = useMemo(
        () => processed.slice(0, SPARKLINE_LIMIT).map((r) => r.symbol),
        [processed],
    );
    const sparklineSymbolSet = useMemo(
        () => new Set(sparklineSymbols),
        [sparklineSymbols],
    );

    // --- Batched sparkline fetch, refreshed when the visible set changes.
    useEffect(() => {
        if (sparklineSymbols.length === 0) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchHistoricalBatch(sparklineSymbols, 90);
                if (cancelled) return;
                // Merge so previously-loaded symbols persist when the
                // visible window shifts (avoids a flash of empty trends).
                setSparklines((prev) => ({ ...prev, ...res }));
            } catch (err) {
                // Sparklines are non-critical; never surface as a page error.
                // eslint-disable-next-line no-console
                console.warn('Failed to load sparkline history', err);
            }
        })();
        return () => {
            cancelled = true;
        };
        // Join key keeps the effect stable when the array identity changes
        // but the contents don't.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sparklineSymbols.join(',')]);

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDesc((d) => !d);
        } else {
            setSortKey(key);
            // Rank/symbol/name/category read best ascending; factor columns
            // best descending (strongest first).
            setSortDesc(
                key !== 'universe_rank' &&
                    key !== 'symbol' &&
                    key !== 'name' &&
                    key !== 'sector',
            );
        }
    };

    const pillBase =
        'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors cursor-pointer';
    const pillActive =
        'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900';
    const pillInactive =
        'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700';

    return (
        <Layout
            sidebar={<Sidebar />}
            title="Commodities Rankings"
            subtitle="Cross-sectional momentum + COT-positioning scores for the futures complex."
        >
            {/* Header row: category filter pills + freshness badge. */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        className={`${pillBase} ${!activeCategory ? pillActive : pillInactive}`}
                        onClick={() => setActiveCategory(null)}
                    >
                        All Categories
                        {totalUniverseSize > 0 && (
                            <span className="ml-1.5 opacity-70">
                                {totalUniverseSize}
                            </span>
                        )}
                    </button>
                    {categories.map((c) => (
                        <button
                            key={c}
                            type="button"
                            className={`${pillBase} ${activeCategory === c ? pillActive : pillInactive}`}
                            onClick={() => setActiveCategory(c)}
                        >
                            {c}
                        </button>
                    ))}
                </div>
                {asOfDate && (
                    <span className="text-xs font-mono text-slate-500 bg-slate-100 dark:bg-slate-800/50 px-2 py-1 rounded">
                        Updated: {asOfDate}
                    </span>
                )}
            </div>

            {error && (
                <div className="mb-6 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-400">
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className="h-64 flex items-center justify-center text-slate-500 dark:text-slate-400">
                    Loading quantitative data…
                </div>
            ) : (
                <>
                    {/* Charts: cross-sectional Risk/Return + category radar. */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
                        <div className="surface-card">
                            <h2 className="section-title mb-1">
                                Risk vs Return
                            </h2>
                            <p className="muted text-xs mb-4">
                                {activeCategory ?? 'All categories'} ·{' '}
                                {processed.length} commodities
                            </p>
                            <RiskReturnScatter
                                data={processed}
                                highlightSymbol={hoverSymbol}
                            />
                        </div>
                        <div className="surface-card">
                            <h2 className="section-title mb-1">
                                Category Factor Radar
                            </h2>
                            <p className="muted text-xs mb-4">
                                Average z-scores by commodity category
                            </p>
                            <CategoryRadar categories={categorySummary} />
                        </div>
                    </div>

                    {/* Full sortable universe table. Dimmed while a
                        server-side category refetch is in flight so the
                        category switch reads as a deliberate reload. */}
                    <div
                        className={`transition-opacity ${isUniverseLoading ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}
                        aria-busy={isUniverseLoading}
                    >
                        <UniverseTable
                            data={processed}
                            sparklines={sparklines}
                            sparklineSymbols={sparklineSymbolSet}
                            sortKey={sortKey}
                            sortDesc={sortDesc}
                            onSort={handleSort}
                            onRowHover={setHoverSymbol}
                            onSymbolClick={setSelectedSymbol}
                        />
                    </div>
                </>
            )}

            {/* COT positioning pop-over for the clicked ticker. */}
            {selectedSymbol && (
                <CotPopup
                    symbol={selectedSymbol}
                    onClose={() => setSelectedSymbol(null)}
                />
            )}
        </Layout>
    );
};

export default CommoditiesRankingPage;
